import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import {
    AppBar,
    Avatar,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import CheckShow from "./shared/CheckShow";

import Colours from "../helpers/colours";
import Icons from "../helpers/icons";
import {
    AROLOGO,
    ARO_ADMIN,
    ARO_ALL,
    HOTELS_INFO,
    getHotelName,
} from "../configuration";

import { setHotelID } from "../actions/hotelID";
import { cleanJS } from "../actions/globalReportFilters";
import { swapToHotelView, swapToOriginal } from "../actions/authRoles";
import { selectHotels } from "../features/hotels/hotelsApi";

const IMAGE_MAX_SIZE = 30;

export const HotelSelector = ({
    value,
    onChange,
    imgMaxWidth = 180,
    compact = false,
    limitedIds,
}) => {
    const dispatch = useDispatch();

    const searchInputRef = useRef(null);
    const selectedItemRef = useRef(null);
    const listRef = useRef(null);
    const activeIndexRef = useRef(0);
    const closingDialogRef = useRef(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { user } = useSelector((state) => state.auth);
    const { id: hotelID = "default" } = useSelector((state) => state.hotelID);
    const { data: hotels = [] } = useSelector(selectHotels);

    const [searchText, setSearchText] = useState("");
    const [options, setOptions] = useState([]);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);

    // value can come from props or Redux
    const controlledHotelID =
        value !== undefined && value !== null ? value : hotelID;

    const isAllowedId = (id) => {
        if (!Array.isArray(limitedIds) || limitedIds.length === 0) return true;
        const allowed = new Set(limitedIds.map((x) => String(x)));
        return allowed.has(String(id));
    };
    const getOptionButtons = () => {
        const root = listRef.current;
        if (!root) return [];
        return Array.from(root.querySelectorAll('[data-hotel-option="true"]'));
    };
    const syncActiveIndexToSelected = () => {
        const buttons = getOptionButtons();
        if (buttons.length === 0) {
            activeIndexRef.current = 0;
            return;
        }

        const idx = buttons.findIndex(
            (b) => String(b.dataset.hotelId) === String(controlledHotelID),
        );
        activeIndexRef.current = idx >= 0 ? idx : 0;
    };

    const focusByIndex = (nextIndex) => {
        const buttons = getOptionButtons();
        if (buttons.length === 0) return;

        const len = buttons.length;
        const safeIndex = ((nextIndex % len) + len) % len;

        activeIndexRef.current = safeIndex;

        const el = buttons[safeIndex];
        el.focus();
        el.scrollIntoView({ block: "nearest" });
    };

    const handleDialogKeyDown = (e) => {
        if (!openDialog) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            focusByIndex(activeIndexRef.current + 1);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            focusByIndex(activeIndexRef.current - 1);
        } else if (e.key === "Enter") {
            const buttons = getOptionButtons();
            const el = buttons[activeIndexRef.current];
            if (el) {
                e.preventDefault();
                el.click();
            }
        }
    };

    const handleDialogEntered = () => {
        // Focus search
        searchInputRef.current?.focus();

        // Scroll selected into view (if exists)
        window.requestAnimationFrame(() => {
            selectedItemRef.current?.scrollIntoView({
                block: "center",
                behavior: "smooth",
            });

            // Added: keep keyboard index in sync with current hotel
            syncActiveIndexToSelected();
        });
    };

    useEffect(() => {
        if (!openDialog) return;

        window.requestAnimationFrame(() => {
            const buttons = getOptionButtons();
            if (buttons.length === 0) {
                activeIndexRef.current = 0;
                return;
            }
            if (activeIndexRef.current >= buttons.length) {
                activeIndexRef.current = 0;
            }
        });
    }, [openDialog, searchText, filteredOptions]);

    useEffect(() => {
        // console.log(filteredOptions);
    }, [filteredOptions]);

    useEffect(() => {
        if (user && user.rolesHotels) {
            let nextOptions = [];

            if ((user.rolesHotels ?? []).includes(-1)) {
                // Remove key 14
                const filteredHotels = Object.entries(HOTELS_INFO)
                    .filter(([key, value]) => key !== "14")
                    .reduce((obj, [key, value]) => {
                        obj[key] = value;
                        return obj;
                    }, {});

                nextOptions = Object.values(filteredHotels);
            } else {
                nextOptions = user.rolesHotels
                    .filter((h) => h in HOTELS_INFO)
                    .map((id) => HOTELS_INFO[id]);
            }

            // Limited mode: keep only allowed ids (if provided)
            nextOptions = nextOptions
                .filter((h) => isAllowedId(h.id))
                .map((hotel) => ({
                    ...hotel,
                    name: getHotelName(hotel.id, hotels),
                }));

            setOptions(nextOptions);
        } else {
            setOptions([]);
        }
    }, [user, limitedIds, hotels]);

    useEffect(() => {
        if (searchText === "") {
            setFilteredOptions(options);
        } else {
            const lowerCaseSearchText = searchText.toLowerCase();

            const results = options.filter(
                (option) =>
                    option.name.toLowerCase().includes(lowerCaseSearchText) ||
                    (option.simplified_name ?? "")
                        .toLowerCase()
                        .includes(lowerCaseSearchText) ||
                    option.id
                        .toString()
                        .toLowerCase()
                        .includes(lowerCaseSearchText),
            );
            setFilteredOptions(results);
        }
    }, [options, searchText]);

    const handleCloseDialog = () => {
        closingDialogRef.current = true;
        setOpenDialog(false);
        setSearchText("");
        setFilteredOptions(options);
    };

    // selector that uses either onChange prop or Redux
    const handleSelectHotel = (selectedId) => {
        if (closingDialogRef.current) return;

        closingDialogRef.current = true;

        if (onChange) {
            onChange(selectedId);
        } else {
            dispatch(setHotelID(selectedId));
            dispatch(cleanJS());
        }

        handleCloseDialog();
    };

    const ListItemComponent = ({ data, hotel = 0 }) => {
        const [expandChild, setExpandChild] = useState(true);

        return (
            <>
                {(!data.hasOwnProperty("isChild") || searchText !== "") && (
                    <ListItem disableGutters key={data.id}>
                        <ListItemButton
                            data-hotel-option="true"
                            data-hotel-id={data.id}
                            onFocus={(e) => {
                                const buttons = getOptionButtons();
                                const idx = buttons.indexOf(e.currentTarget);
                                if (idx >= 0) activeIndexRef.current = idx;
                            }}
                            onClick={() => {
                                handleSelectHotel(data.id);
                            }}
                            ref={
                                data.id === hotel ? selectedItemRef : undefined
                            }
                            sx={{
                                backgroundColor:
                                    data.id === hotel
                                        ? Colours.selectedRow
                                        : null,
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    alt={`Hotel Logo ${data.name}`}
                                    src={data.img}
                                />
                            </ListItemAvatar>
                            <CheckShow
                                rolesThatCanSee={ARO_ADMIN}
                                alternative={
                                    <ListItemText
                                        primary={
                                            isMobile
                                                ? data.name.substring(0, 20) +
                                                  "..."
                                                : data.name
                                        }
                                    />
                                }
                            >
                                <ListItemText
                                    secondary={data.id}
                                    primary={
                                        isMobile
                                            ? data.name.substring(0, 20) + "..."
                                            : data.name
                                    }
                                />
                            </CheckShow>
                        </ListItemButton>
                        {data.hasOwnProperty("parent") && (
                            <Button
                                onClick={() => {
                                    setExpandChild(!expandChild);
                                }}
                                sx={{ color: Colours.transparentBubble }}
                            >
                                {expandChild ? (
                                    <Icons.ArrowDown />
                                ) : (
                                    <Icons.ArrowUp />
                                )}
                            </Button>
                        )}
                    </ListItem>
                )}

                {data.hasOwnProperty("parent") && (
                    <Collapse in={expandChild} timeout="auto" unmountOnExit>
                        <List>
                            {data.parent
                                .filter((id) => isAllowedId(id))
                                .filter(
                                    (id) =>
                                        user.rolesHotels.includes(-1) ||
                                        user.rolesHotels.includes(id),
                                )
                                .map((id) => ({
                                    ...HOTELS_INFO[id],
                                    name: getHotelName(id, hotels, false),
                                }))
                                .map((subHotel) => (
                                    <ListItemButton
                                        key={subHotel.id}
                                        data-hotel-option="true"
                                        data-hotel-id={subHotel.id}
                                        onFocus={(e) => {
                                            const buttons = getOptionButtons();
                                            const idx = buttons.indexOf(
                                                e.currentTarget,
                                            );
                                            if (idx >= 0)
                                                activeIndexRef.current = idx;
                                        }}
                                        sx={{
                                            py: 1,
                                            pl: "2em",
                                            backgroundColor:
                                                subHotel.id === hotel
                                                    ? Colours.selectedRow
                                                    : null,
                                        }}
                                        onClick={() => {
                                            // Changed: use generic handler
                                            handleSelectHotel(subHotel.id);
                                        }}
                                        ref={
                                            subHotel.id === hotel
                                                ? selectedItemRef
                                                : undefined
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                alt={`Hotel Logo ${subHotel.name}`}
                                                src={subHotel.img ?? ""}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            secondary={subHotel.id}
                                            primary={
                                                isMobile
                                                    ? subHotel.name.substring(
                                                          0,
                                                          20,
                                                      ) + "..."
                                                    : subHotel.name
                                            }
                                        />
                                    </ListItemButton>
                                ))}
                        </List>
                    </Collapse>
                )}
            </>
        );
    };

    return (
        <>
            <Button
                variant="text"
                sx={{
                    textTransform: "none",
                    "&:hover": {
                        backgroundColor: Colours.hoverGrey,
                    },
                }}
                onClick={() => {
                    closingDialogRef.current = false;
                    setOpenDialog(true);
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                >
                    {/* Main button now uses controlledHotelID */}
                    <Box
                        sx={{
                            height: "2em",
                            pr: 1,
                            maxWidth: imgMaxWidth, // keep the cap
                            display: "inline-flex",
                            alignItems: "center",
                            overflow: "visible", // don't crop the image
                            flexShrink: 0, // avoid squeezing it
                        }}
                    >
                        <img
                            src={
                                (HOTELS_INFO[controlledHotelID] ?? {}).img ?? ""
                            }
                            alt={`Hotel Logo ${getHotelName(
                                controlledHotelID,
                                hotels,
                            )}`}
                            style={{
                                height: "100%", // scale by height
                                width: "auto", // keep aspect ratio
                                maxWidth: `${imgMaxWidth}px`, // never exceed this
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    </Box>
                    {!compact && (
                        <>
                            <Typography>
                                {isMobile
                                    ? getHotelName(
                                          controlledHotelID,
                                          hotels,
                                          false,
                                      ).substring(0, 20) + "..."
                                    : getHotelName(
                                          controlledHotelID,
                                          hotels,
                                          false,
                                      )}
                            </Typography>
                        </>
                    )}
                    <Icons.ArrowDown />
                </Stack>
            </Button>
            <Dialog
                onClose={handleCloseDialog}
                open={openDialog}
                TransitionProps={{
                    onEntered: handleDialogEntered,
                    onExited: () => {
                        closingDialogRef.current = false;
                    },
                }}
                onKeyDown={handleDialogKeyDown}
            >
                <DialogTitle>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                    >
                        <Typography variant="h6">Select Hotel</Typography>
                        <TextField
                            inputRef={searchInputRef}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icons.Search />
                                    </InputAdornment>
                                ),
                            }}
                            onChange={(event) =>
                                setSearchText(event.target.value)
                            }
                            variant="standard"
                        />
                        <IconButton onClick={handleCloseDialog}>
                            <Icons.CloseNavbar />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <List ref={listRef} sx={{ pt: 0 }}>
                        {searchText === "" &&
                            [...filteredOptions]
                                .sort((a, b) => (a.name > b.name ? 1 : -1))
                                .map((data) => (
                                    <ListItemComponent
                                        key={data.id}
                                        data={data}
                                        hotel={controlledHotelID}
                                    />
                                ))}

                        {filteredOptions.length === 0 && (
                            <React.Fragment>
                                <Divider
                                    sx={{
                                        borderColor: Colours.navbarBottomLine,
                                    }}
                                />
                                <Typography fontSize={"0.8em"}>
                                    No results
                                </Typography>
                            </React.Fragment>
                        )}

                        {searchText !== "" && filteredOptions.length > 0 && (
                            <>
                                <React.Fragment>
                                    <Divider
                                        sx={{
                                            borderColor:
                                                Colours.navbarBottomLine,
                                        }}
                                    />
                                    <Typography fontSize={"0.8em"}>
                                        Main results
                                    </Typography>
                                </React.Fragment>
                                {filteredOptions.slice(0, 4).map((data) => (
                                    <ListItemComponent
                                        key={data.id}
                                        data={data}
                                        hotel={controlledHotelID}
                                    />
                                ))}
                            </>
                        )}

                        {searchText !== "" && filteredOptions.length > 4 && (
                            <>
                                <React.Fragment>
                                    <Divider
                                        sx={{
                                            borderColor:
                                                Colours.navbarBottomLine,
                                        }}
                                    />
                                    <Typography fontSize={"0.8em"}>
                                        Similar results
                                    </Typography>
                                </React.Fragment>
                                {filteredOptions.slice(4).map((data) => (
                                    <ListItemComponent
                                        key={data.id}
                                        data={data}
                                        hotel={controlledHotelID}
                                    />
                                ))}
                            </>
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
};

const AdminHotelSelector = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const view = isMobile ? "" : " view";

    const [mode, setMode] = useState("Admin");

    return (
        <Button
            sx={{
                textTransform: "none",
                "&:hover": {
                    backgroundColor: Colours.hoverGrey,
                },
            }}
            onClick={() => {
                if (mode === "Admin") {
                    setMode("Hotel");
                    dispatch(swapToHotelView());
                } else {
                    setMode("Admin");
                    dispatch(swapToOriginal());
                }
            }}
        >
            <Typography>
                {mode} {view}
            </Typography>
        </Button>
    );
};

const handleLogOut = (navigate) => {
    localStorage.clear();
    navigate("/login");
    window.location.reload(false);
};

const OptionsButtons = () => {
    const navigate = useNavigate();

    return (
        <>
            <Tooltip title="Log Out">
                <IconButton
                    aria-label="log out"
                    onClick={() => handleLogOut(navigate)}
                >
                    <Icons.LogOut />
                </IconButton>
            </Tooltip>
            {/* <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={handleClick}
            >
                <Icons.Options />
            </IconButton>
            <Menu
                id="long-menu"
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                {OPTIONS.map((option, index) => (
                    <MenuItem
                        key={index}
                        onClick={() => {
                            handleClose();
                            option.action();
                        }}
                    >
                        <option.Icon
                            color={option.active ?? false ? "primary" : ""}
                            sx={{ marginRight: 1 }}
                        />
                        {option.name}
                    </MenuItem>
                ))}
            </Menu> */}

            {/*  _        _    _   _  ____  */}
            {/* | |      / \  | \ | |/ ___| */}
            {/* | |     / _ \ |  \| | |  _  */}
            {/* | |___ / ___ \| |\  | |_| | */}
            {/* |_____/_/   \_\_| \_|\____| */}
            {/* <Popover
                id="popoverLang"
                open={openPopoverLanguage}
                anchorEl={anchorEl}
                onClose={() => setOpenPopoverLanguage(false)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Container sx={{ m: 2 }}>
                    <Typography variant="h6">{t("language")}</Typography>
                    <RadioGroup
                        value={i18n.language}
                        onChange={(event) => {
                            i18n.changeLanguage(event.target.value);
                            setOpenPopoverLanguage(false);
                        }}
                    >
                        <FormControlLabel
                            value="en"
                            control={<Radio />}
                            label="🇬🇧 English"
                        />
                        <FormControlLabel
                            value="es"
                            control={<Radio />}
                            label="🇪🇸 Español"
                        />
                    </RadioGroup>
                </Container>
            </Popover> */}
        </>
    );
};

const TopBar = ({ actualPage, title }) => {
    const theme = useTheme();
    let isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <AppBar
            position="fixed"
            style={{
                background: "#fafafa", //   "transparent"
                boxShadow: "none",
                borderBottom: `1px solid ${Colours.navbarBottomLine}`,
                zIndex: 1200,
            }}
        >
            <Toolbar>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    wrap="nowrap" // Asegura que los ítems no se envuelvan
                    sx={{
                        overflowX: "auto", // Permite desplazamiento horizontal si los ítems no caben
                    }}
                >
                    <Grid item>
                        <Link
                            to="/app"
                            underline="none"
                            style={{
                                textDecoration: "none",
                                color: Colours.textColour,
                            }}
                        >
                            <Box
                                component="img"
                                sx={{
                                    maxHeight: {
                                        xs: IMAGE_MAX_SIZE,
                                        md: IMAGE_MAX_SIZE,
                                    },
                                }}
                                alt="Aró Analytics BETA Logo"
                                src={isMobile ? "/aab-favicon.svg" : AROLOGO}
                            />
                        </Link>
                    </Grid>
                    <Grid item xs />
                    <Grid item>
                        <HotelSelector compact={isMobile} />
                    </Grid>
                    <CheckShow rolesThatCanSee={ARO_ALL} useEditedRoles={false}>
                        <Grid item>
                            <AdminHotelSelector />
                        </Grid>
                    </CheckShow>
                    <Grid item>
                        <OptionsButtons />
                    </Grid>
                </Grid>
            </Toolbar>
        </AppBar>
    );
};

export default TopBar;
